import { useEffect } from 'react';
import { StoreObject, useApolloClient } from '@apollo/client';
import { print } from 'graphql/language/printer';
import { useWsSubscriptionContext } from './useWsSubscriptionContext';
import {
  deviceSubscriptionUpdate,
  projectSubscriptionUpdate,
  testSubscriptionUpdate,
} from '~src/queries/codegen/fragments';
import { deviceSubscription } from '~src/queries/codegen/deviceSubscription';
import { projectSubscription } from '~src/queries/codegen/projectSubscription';
import { testSubscription } from '~src/queries/codegen/testSubscription';
import { testScriptStatus } from '~src/queries/codegen/testScriptStatusSubscription';
import { TestScriptStatusSubscriptionVariables } from '~src/generated/portal/graphql';
import GET_TEST_SCRIPT_STATUS from '~src/queries/codegen/adkTestScriptStatus';

// TODO
const unsubscribe = (ws: WebSocket | null, ready: boolean) =>
  ws && ready
    ? () => {
        // eslint-disable-next-line no-console
        console.log('ToDo unsubscribe');
      }
    : undefined;

const useDeviceSubscription = ({ projectId }: { projectId: string }) => {
  const { ws, ready } = useWsSubscriptionContext();

  useEffect(() => {
    if (ws && projectId && ready)
      ws.send(JSON.stringify({ query: print(deviceSubscription), variables: JSON.stringify({ projectId }) }));
    return unsubscribe(ws, ready);
  }, [ready, projectId, ws]);
};

const useProjectSubscription = ({ partnerId }: { partnerId: string }) => {
  const { ws, ready } = useWsSubscriptionContext();
  useEffect(() => {
    if (ws && partnerId && ready)
      ws.send(JSON.stringify({ query: print(projectSubscription), variables: JSON.stringify({ partnerId }) }));
    return unsubscribe(ws, ready);
  }, [ready, partnerId, ws]);
};

const useTestSubscription = ({ projectId }: { projectId: string }) => {
  const { ws, ready } = useWsSubscriptionContext();
  useEffect(() => {
    if (ws && projectId && ready)
      ws.send(JSON.stringify({ query: print(testSubscription), variables: JSON.stringify({ projectId }) }));

    return unsubscribe(ws, ready);
  }, [ready, projectId, ws]);
};

const useTestScriptSubscription = ({ projectId, deviceId, testId }: TestScriptStatusSubscriptionVariables) => {
  const { ws, ready } = useWsSubscriptionContext();
  useEffect(() => {
    if (ws && projectId && deviceId && testId && ready)
      ws.send(
        JSON.stringify({
          query: print(testScriptStatus),
          variables: JSON.stringify({ projectId, deviceId, testId }),
        }),
      );

    return unsubscribe(ws, ready);
  }, [ready, projectId, deviceId, testId, ws]);
};

const sendSubscriptions = (projectId: string, partnerId: string, ws: WebSocket) => {
  ws.send(JSON.stringify({ query: print(testSubscription), variables: JSON.stringify({ projectId }) }));
  ws.send(JSON.stringify({ query: print(projectSubscription), variables: JSON.stringify({ partnerId }) }));
  ws.send(JSON.stringify({ query: print(deviceSubscription), variables: JSON.stringify({ projectId }) }));
};

const useSubscriptions = (projectId: string, partnerId: string) => {
  const { ws, ready } = useWsSubscriptionContext();
  useEffect(() => {
    if (ws && projectId && projectId && ready) sendSubscriptions(projectId, partnerId, ws);
  }, [partnerId, projectId, ready, ws]);
};

const useMessageHandler = (ws: WebSocket | null) => {
  const client = useApolloClient();
  useEffect(() => {
    if (ws) {
      ws.onmessage = (msg) => {
        try {
          const eventData = msg.data === 'subscription accepted.' ? 'rawEventData' : JSON.parse(msg.data);

          if (eventData?.data?.projectUpdated) {
            const data = eventData?.data;
            if (!data.projectUpdated) return;
            const { project } = data.projectUpdated;
            client.writeFragment({
              id: client.cache.identify({ ...project, __typename: 'Project' } as unknown as StoreObject),
              fragment: projectSubscriptionUpdate,
              data: { ...project, __typename: 'Project' },
            });
            return;
          }

          if (eventData?.data?.testUpdated) {
            const data = eventData?.data;
            if (!data.testUpdated) return;
            const { testUpdated } = data;
            const test = {
              ...testUpdated.test,
              __typename: 'ADKTestWithStatus',
            };

            client.writeFragment({
              id: client.cache.identify(test as unknown as StoreObject),
              fragment: testSubscriptionUpdate,
              data: test,
            });
            return;
          }

          if (eventData?.data?.deviceUpdated) {
            const data = eventData?.data;
            const { device } = data.deviceUpdated;

            client.writeFragment({
              id: client.cache.identify({ ...device, __typename: 'Device' } as unknown as StoreObject),
              fragment: deviceSubscriptionUpdate,
              data: { ...device, __typename: 'Device' },
            });

            return;
          }

          if (eventData?.data?.testScriptStatusUpdated) {
            const id = eventData?.data?.testScriptStatusUpdated.id;
            if (!id) return;
            const [projectId, deviceId, testId] = id.split(':');
            const testScriptStatusPrev = client.readQuery({
              query: GET_TEST_SCRIPT_STATUS,
              variables: { projectId, deviceId, testId },
            });

            if (testScriptStatusPrev) {
              // TODO verify if this is needed
              /* const tests = [];
              for (let i = 0; i < (testScriptStatusPrev?.adkTestScriptStatus?.tests || []).length; i++) {
                const test = testScriptStatusPrev?.adkTestScriptStatus?.tests[i] as ScriptTestStatus;
                tests[i] = { ...test, actions: [] as ScriptActionStatus[] };
                for (let j = 0; j < (test?.actions || []).length; j++) {
                  const action = test?.actions[j];
                  tests[i].actions[j] = {
                    ...action,
                    status: eventData?.data?.testScriptStatusUpdated?.testScriptStatus.tests[i].actions[j].status,
                  };
                }
              }
              console.group('testScript Sub update');
              console.log(
                testScriptStatusPrev.adkTestScriptStatus?.tests,
                eventData?.data?.testScriptStatusUpdated?.testScriptStatus.tests,
                tests,
              );
              console.groupEnd(); */

              client.cache.writeQuery({
                query: GET_TEST_SCRIPT_STATUS,
                variables: { projectId, deviceId, testId },
                data: {
                  ...eventData.data.testScriptStatusUpdated,
                },
                overwrite: true,
              });

              return;
            }
          }
        } catch (err) {
          console.error(err); // eslint-disable-line no-console
        }
      };
    }
  }, [client, ws]);
};

export {
  useMessageHandler,
  useDeviceSubscription,
  useProjectSubscription,
  useTestSubscription,
  sendSubscriptions,
  useSubscriptions,
  useTestScriptSubscription,
};
